import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import {
  BackgroundColors,
  ControlColors,
  PrimaryColors,
  ErrorColors,
  TextColors,
} from '@picsart/design-system/foundation/colors';
import { Shadows } from '@picsart/design-system/foundation/shadows';
import { Effects } from '@picsart/design-system/foundation/effects';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import {
  FontSizes,
  LineHeights,
} from '@picsart/design-system/foundation/typography';
import { Easings } from '@picsart/design-system/foundation/easings';
import { Breakpoints } from '@picsart/design-system/foundation/responsive';
import { canHover } from '@picsart/design-system/styleUtils/hoverTouch';

type StyleProps = {
  hasError?: boolean;
};

const useStyles = createUseStyles(
  {
    '@keyframes bounceIn': {
      '0%': {
        transform: 'scale(1)',
      },
      '50%': {
        transform: 'scale(1.02)',
      },
      '100%': {
        transform: 'scale(1)',
      },
    },
    dropzone: {
      display: 'flex',
      flexDirection: 'column',
      padding: [Spacings.s16, Spacings.s12],
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      cursor: 'pointer',
      // No DS easing match for cubic-bezier(0.4, 0, 0.2, 1) — Material standard curve
      transition: `background-color 0.25s ${Easings.easeOut}, box-shadow 0.25s ${Easings.easeOut}`,
      backgroundColor: BackgroundColors.base,
      borderRadius: CornerRadius.r12,
      WebkitTapHighlightColor: 'transparent',
      minHeight: 120,
      position: 'relative',
      minWidth: 0, // Allow grid items to shrink below content size
      width: '100%', // Fill container regardless of its display type
      boxSizing: 'border-box',
      // SVG-based dashed border - grey by default, gradient on hover
      backgroundImage: ({ hasError }: StyleProps) =>
        hasError
          ? `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23ef4444' stroke-width='1.5' stroke-dasharray='3%2c 4' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`
          : `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%236B7280' stroke-width='1.5' stroke-dasharray='3%2c 4' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`,
      // Hover state - gradient border appears (desktop only)
      [canHover]: {
        '&:hover': {
          backgroundColor: BackgroundColors.tint2,
          boxShadow: Shadows.tint2,
          backgroundImage: ({ hasError }: StyleProps) =>
            hasError
              ? `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23ef4444' stroke-width='2' stroke-dasharray='3%2c 4' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`
              : `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3clinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3e%3cstop offset='0%25' stop-color='%23E91E8C'/%3e%3cstop offset='50%25' stop-color='%23B24BC4'/%3e%3cstop offset='100%25' stop-color='%239C27B0'/%3e%3c/linearGradient%3e%3c/defs%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='url(%23g)' stroke-width='2' stroke-dasharray='3%2c 4' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`,
        },
      },
      [`@media (max-width: ${Breakpoints.xs}px)`]: {
        padding: [Spacings.s16, Spacings.s10],
        minHeight: 120,
      },
    },
    // Shared hover/drag active state styles
    dropzoneDragging: {
      backgroundColor: BackgroundColors.tint2,
      boxShadow: Shadows.tint2,
      backgroundImage: ({ hasError }: StyleProps) =>
        hasError
          ? `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23ef4444' stroke-width='2' stroke-dasharray='3%2c 4' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`
          : `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3clinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3e%3cstop offset='0%25' stop-color='%23E91E8C'/%3e%3cstop offset='50%25' stop-color='%23B24BC4'/%3e%3cstop offset='100%25' stop-color='%239C27B0'/%3e%3c/linearGradient%3e%3c/defs%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='url(%23g)' stroke-width='2' stroke-dasharray='3%2c 4' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`,
      '& $text': {
        color: PrimaryColors.base.default,
      },
      '& $iconWrapper': {
        transform: 'scale(1.05)',
      },
      '& $icon': {
        color: PrimaryColors.base.default,
      },
    },
    // Shake animation for invalid file type (gentle)
    '@keyframes shake': {
      '0%, 100%': { transform: 'translateX(0)' },
      '25%, 75%': { transform: 'translateX(-2px)' },
      '50%': { transform: 'translateX(2px)' },
    },
    // Invalid file type drag state. Error semantics come from the dashed
    // SVG border + text color; the wash is a subtle theme-aware tint.
    dropzoneInvalid: {
      backgroundColor: Effects.alpha20,
      animation: `$shake 0.3s ${Easings.easeInOut}`,
      backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23ef4444' stroke-width='2' stroke-dasharray='3%2c 4' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`,
      '& $text': {
        color: ErrorColors.base.default,
      },
      '& $hint': {
        color: ErrorColors.base.default,
        opacity: 0.8,
      },
      '& $iconWrapper': {
        transform: 'scale(1.1) rotate(-10deg)',
        transition: `transform 0.2s ${Easings.ease}`,
      },
      '& $icon': {
        color: ErrorColors.base.default,
      },
    },
    wrapper: {
      width: '100%',
      height: '100%',
      minWidth: 0, // Allow grid items to shrink
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: Spacings.s4,
      padding: [0, Spacings.s8],
      textAlign: 'center',
      [`@media (max-width: ${Breakpoints.xs}px)`]: {
        gap: Spacings.s4,
        padding: [0, Spacings.s4],
      },
    },
    iconWrapper: {
      borderRadius: CornerRadius.r12,
      backgroundColor: BackgroundColors.tint2,
      padding: Spacings.s12,
      // No DS easing match for cubic-bezier(0.4, 0, 0.2, 1) — Material standard curve
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: Shadows.tint1,
      // Hover state (desktop only)
      [canHover]: {
        '$dropzone:hover &': {
          transform: 'scale(1.05)',
          // Background color stays the same on hover
        },
      },
      '$dropzoneDragging &': {
        transform: 'scale(1.1)',
        animation: `$bounceIn 0.3s ${Easings.ease}`,
      },
      [`@media (max-width: ${Breakpoints.xs}px)`]: {
        padding: Spacings.s10,
      },
    },
    icon: {
      width: 24,
      height: 24,
      color: TextColors.tint1,
      transition: `color 0.25s ${Easings.ease}`,
      // Hover state (desktop only)
      [canHover]: {
        '$dropzone:hover &': {
          color: PrimaryColors.base.default,
        },
      },
      '$dropzoneDragging &': {
        color: PrimaryColors.base.default,
      },
      [`@media (max-width: ${Breakpoints.xs}px)`]: {
        width: 20,
        height: 20,
      },
    },
    text: {
      fontSize: FontSizes.t4,
      fontWeight: FontWeights.semibold,
      lineHeight: LineHeights.t4,
      color: ControlColors.text.default,
      transition: `color 0.2s ${Easings.ease}`,
      marginTop: Spacings.s2,
      marginBottom: -2,
      whiteSpace: 'nowrap',
      // Hover state (desktop only)
      [canHover]: {
        '$dropzone:hover &': {
          color: PrimaryColors.base.default,
        },
      },
      [`@media (max-width: ${Breakpoints.xs}px)`]: {
        fontSize: FontSizes.t3,
        marginTop: 0,
      },
    },
    hint: {
      fontSize: FontSizes.t3,
      color: TextColors.tint1,
      lineHeight: LineHeights.t3,
      opacity: 0.7,
      [`@media (max-width: ${Breakpoints.xs}px)`]: {
        fontSize: FontSizes.t2,
      },
    },
    dropzoneWrapper: {
      position: 'relative',
      display: 'block',
      width: '100%',
      minHeight: 'inherit',
    },
  },
  { name: 'drop-zone' }
);

export default useStyles;
